import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { LOGO_ICON, ROUTE_CONTACT } from "@/lib/brand";

const SIDEBAR_ITEMS = [{ id: "top", label: "Start", icon: Clock }];

const BACK_HOME = { label: "Zurück zur Startseite", to: "/", back: true };
const CONTACT = { label: "Kontakt aufnehmen", to: ROUTE_CONTACT };

/**
 * Platzhalterseite für Inhalte, die noch nicht stehen. Nutzt dieselbe
 * Bildsprache wie die übrigen Seiten, bleibt aber bewusst auf einen Bildschirm
 * beschränkt. `tagline` steht im Verlauf unter dem Titel, `primary` und
 * `secondary` sind { label, to, back }; `back` setzt den Pfeil nach links.
 */
export const ComingSoon = ({
  title,
  eyebrow,
  tagline = "Coming Soon",
  description,
  note,
  primary = BACK_HOME,
  secondary = CONTACT,
}) => (
  <PageLayout sidebarItems={SIDEBAR_ITEMS} title={title} testId="coming-soon-main">
    <section
      id="top"
      data-testid="coming-soon-section"
      className="relative min-h-[calc(100vh-var(--header-h))] flex items-center pt-32 pb-24 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 w-[480px] h-[480px] rounded-full bg-brand-orange/20 blur-[150px] animate-pulseGlow" />
        <div className="absolute bottom-1/4 right-1/4 w-[480px] h-[480px] rounded-full bg-brand-blue/20 blur-[150px] animate-pulseGlow" />
      </div>

      <div className="w-full px-6 md:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto max-w-3xl text-center"
        >
          {/* Als Block, sonst teilt sich die Bildmarke die Zeile mit dem
              Kicker darunter und schiebt ihn aus der Mitte. */}
          <div className="relative w-fit mx-auto mb-10">
            <div className="absolute inset-[-40%] rounded-full border border-white/10 animate-spinSlow" />
            <div className="absolute inset-[-70%] rounded-full border border-dashed border-white/[0.07]" />
            <img
              src={LOGO_ICON}
              alt=""
              aria-hidden="true"
              className="relative w-20 h-20 object-contain animate-float drop-shadow-[0_0_25px_rgba(0,122,255,0.5)]"
            />
          </div>

          {eyebrow && (
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-7">
              <span className="w-2 h-2 rounded-full bg-brand-orange shadow-[0_0_10px_2px_rgba(255,107,0,0.8)]" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-white/70">
                {eyebrow}
              </span>
            </div>
          )}

          <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95]">
            {title}
          </h1>
          <p className="mt-5 font-display font-bold text-2xl md:text-3xl gradient-text">
            {tagline}
          </p>

          {description && (
            <p className="mt-7 mx-auto max-w-xl text-base md:text-lg leading-relaxed text-white/55">
              {description}
            </p>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={primary.to}
              data-testid="coming-soon-primary-link"
              className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_35px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_45px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.04] transition-all duration-300"
            >
              {primary.back && (
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
              )}
              {primary.label}
              {!primary.back && (
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
              )}
            </Link>
            <Link
              to={secondary.to}
              data-testid="coming-soon-secondary-link"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300"
            >
              {secondary.label}
            </Link>
          </div>

          {note && <p className="mt-8 text-xs text-white/35">{note}</p>}
        </motion.div>
      </div>
    </section>
  </PageLayout>
);
